import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Spin, Empty } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useForecastFields } from '../../../hooks/useForecastFields';
import forecastService, {
  type ForecastDaily,
  type ForecastHourly,
} from '../../../api/forecastService';
import { getEventSource, isEventActive as checkEventActive } from '../../../utils/eventSource';
import { useCollectionFreshness } from '../../../hooks/useCollectionFreshness';
import {
  ForecastChartView,
  type ForecastChartDisplaySettings,
  type ForecastChartWidgetMode,
} from '../../../components/Forecast/ForecastChartView';
import { usePlayerOptional } from '../usePlayerContext';
import type { WidgetProps } from './types';

export interface ForecastChartWidgetSettings extends ForecastChartDisplaySettings {
  forecastMode: ForecastChartWidgetMode;
  filterBySession: boolean;
  hoursBeforeSession: number;
}

function resolveForecastType(
  selectedDate: Date | null,
  dailyForecasts: ForecastDaily[],
  hourlyForecasts: ForecastHourly[],
  preference: ForecastChartWidgetMode,
): 'hourly' | 'daily' | 'none' {
  if (preference === 'daily') {
    return dailyForecasts.length > 0 ? 'daily' : 'none';
  }
  if (preference === 'hourly') {
    return hourlyForecasts.length > 0 ? 'hourly' : 'none';
  }

  if (!selectedDate) {
    if (hourlyForecasts.length > 0) return 'hourly';
    if (dailyForecasts.length > 0) return 'daily';
    return 'none';
  }

  const selectedTime = selectedDate.getTime();

  let latestDailyIssueTime: number | null = null;
  if (dailyForecasts.length > 0) {
    const validDailyForecasts = dailyForecasts.filter((f) => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });
    if (validDailyForecasts.length > 0) {
      latestDailyIssueTime = Math.max(
        ...validDailyForecasts.map((f) => new Date(f.value_date).getTime()),
      );
    }
  }

  let latestHourlyIssueTime: number | null = null;
  if (hourlyForecasts.length > 0) {
    const validHourlyForecasts = hourlyForecasts.filter((f) => {
      const issueTime = new Date(f.value_date).getTime();
      return issueTime <= selectedTime;
    });
    if (validHourlyForecasts.length > 0) {
      latestHourlyIssueTime = Math.max(
        ...validHourlyForecasts.map((f) => new Date(f.value_date).getTime()),
      );
    }
  }

  if (latestDailyIssueTime === null && latestHourlyIssueTime === null) {
    return 'none';
  }
  if (latestDailyIssueTime !== null && latestHourlyIssueTime === null) {
    return 'daily';
  }
  if (latestHourlyIssueTime !== null && latestDailyIssueTime === null) {
    return 'hourly';
  }
  if (latestHourlyIssueTime! >= latestDailyIssueTime!) {
    return 'hourly';
  }
  return 'daily';
}

export const ForecastChartWidget = ({
  config,
  onConfigChange,
  openSettingsTrigger,
  playerTimestamp,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const player = usePlayerOptional();
  const {
    dailyTableFields,
    hourlyTableFields,
    loading: fieldsLoading,
  } = useForecastFields();

  const isEventActive = checkEventActive(selectedEvent);
  const metadataTimestamps = useCollectionFreshness(['forecast_daily', 'forecast_hourly'], {
    interval: 30_000,
    enabled: isEventActive,
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const lastChartFetchRef = useRef<string>('');
  useEffect(() => {
    const latest = metadataTimestamps.forecast_daily || metadataTimestamps.forecast_hourly;
    if (latest && latest > lastChartFetchRef.current) {
      lastChartFetchRef.current = latest;
      setRefreshCounter(c => c + 1);
    }
  }, [metadataTimestamps]);

  const selectedDateRange = player?.selectedDateRange ?? null;
  const chartSettings = (config.chartSettings as Partial<ForecastChartWidgetSettings> | undefined) ?? undefined;

  const forecastMode = chartSettings?.forecastMode ?? 'auto';
  const filterBySession = chartSettings?.filterBySession ?? false;
  const hoursBeforeSession = chartSettings?.hoursBeforeSession ?? 2;

  const compactActionsRef = useRef<{
    openSettings: () => void;
    openFullScreen: () => void;
  } | null>(null);

  useEffect(() => {
    if (openSettingsTrigger && openSettingsTrigger > 0) {
      compactActionsRef.current?.openSettings();
    }
  }, [openSettingsTrigger]);

  const handleDisplaySettingsChange = useCallback(
    (display: ForecastChartDisplaySettings) => {
      onConfigChange?.({
        chartSettings: {
          forecastMode,
          filterBySession,
          hoursBeforeSession,
          ...chartSettings,
          ...display,
        },
      });
    },
    [onConfigChange, chartSettings, forecastMode, filterBySession, hoursBeforeSession],
  );

  const handleWidgetConfigChange = useCallback(
    (partial: Partial<Pick<ForecastChartWidgetSettings, 'forecastMode' | 'filterBySession' | 'hoursBeforeSession'>>) => {
      onConfigChange?.({
        chartSettings: {
          forecastMode,
          filterBySession,
          hoursBeforeSession,
          ...chartSettings,
          ...partial,
        },
      });
    },
    [onConfigChange, chartSettings, forecastMode, filterBySession, hoursBeforeSession],
  );

  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    isFirstLoadRef.current = true;
    setDailyForecasts([]);
    setHourlyForecasts([]);
    setLoading(false);
    setError(null);
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedEvent) return;

    let cancelled = false;
    const isFirstLoad = isFirstLoadRef.current;

    if (isFirstLoad) {
      setLoading(true);
      setError(null);
    }

    const fetchData = async () => {
      try {
        const source = getEventSource(selectedEvent);
        const [dailyResponse, hourlyResponse] = await Promise.allSettled([
          forecastService.getForecastDaily({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          forecastService.getForecastHourly({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ]);

        if (cancelled) return;

        const daily =
          dailyResponse.status === 'fulfilled'
            ? dailyResponse.value.forecasts
            : [];
        const hourly =
          hourlyResponse.status === 'fulfilled'
            ? hourlyResponse.value.forecasts
            : [];

        setDailyForecasts(daily);
        setHourlyForecasts(hourly);
        setError(null);

        if (isFirstLoad) {
          isFirstLoadRef.current = false;
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          if (isFirstLoad) {
            setError(
              err instanceof Error ? err.message : 'Failed to load forecast data',
            );
            setLoading(false);
          } else {
            console.warn('Forecast chart silent refresh failed:', err);
          }
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [selectedEvent, refreshCounter]);

  const selectedDate = useMemo(() => {
    if (playerTimestamp) return playerTimestamp;
    if (selectedDateRange) return selectedDateRange.start;
    return null;
  }, [playerTimestamp, selectedDateRange]);

  const forecastType = useMemo(
    () =>
      resolveForecastType(
        selectedDate,
        dailyForecasts,
        hourlyForecasts,
        forecastMode,
      ),
    [selectedDate, dailyForecasts, hourlyForecasts, forecastMode],
  );

  /** Resolved type for rendering — keeps settings accessible when forced mode has no data */
  const displayForecastType = useMemo((): 'daily' | 'hourly' => {
    if (forecastMode === 'hourly') return 'hourly';
    if (forecastMode === 'daily') return 'daily';
    if (forecastType === 'hourly') return 'hourly';
    if (forecastType === 'daily') return 'daily';
    if (hourlyForecasts.length > 0) return 'hourly';
    return 'daily';
  }, [forecastMode, forecastType, hourlyForecasts.length]);

  const sessions = useMemo(
    () => selectedEvent?.sessions || [],
    [selectedEvent?.sessions],
  );

  const chartForecasts = useMemo(() => {
    if (displayForecastType === 'hourly') return hourlyForecasts;
    return dailyForecasts;
  }, [displayForecastType, hourlyForecasts, dailyForecasts]);

  const availableFields = useMemo(() => {
    if (displayForecastType === 'hourly') return hourlyTableFields;
    return dailyTableFields;
  }, [displayForecastType, hourlyTableFields, dailyTableFields]);

  const hasForecastData = forecastType !== 'none' && chartForecasts.length > 0;

  if ((loading || fieldsLoading) && dailyForecasts.length === 0 && hourlyForecasts.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>No event selected</span>}
        />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 11 }}>Select a time range on the timeline</span>
          }
        />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>{error}</span>}
        />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <ForecastChartView
        forecasts={chartForecasts}
        selectedDate={selectedDate}
        forecastType={displayForecastType}
        availableFields={availableFields}
        disableAnimations
        filterBySession={forecastType === 'hourly' ? filterBySession : false}
        hoursBeforeSession={hoursBeforeSession}
        sessions={sessions}
        variant="compact"
        embeddedInWidget
        hideFullScreen
        compactActionsRef={compactActionsRef}
        settings={chartSettings}
        onSettingsChange={handleDisplaySettingsChange}
        widgetConfig={{
          forecastMode,
          filterBySession,
          hoursBeforeSession,
          activeForecastType: displayForecastType,
        }}
        onWidgetConfigChange={handleWidgetConfigChange}
        emptyMessage={
          hasForecastData
            ? undefined
            : forecastMode === 'hourly' && hourlyForecasts.length === 0
              ? 'No hourly forecast data available'
              : forecastMode === 'daily' && dailyForecasts.length === 0
                ? 'No daily forecast data available'
                : 'No forecast data available'
        }
      />
    </div>
  );
};
